'use client'

import { Button } from '@/components/ui/button'
import { ChatbotView } from '@/components/ChatbotView'
import { formatContent, getButtonIcon, handleButtonClick } from '@/utils/micrositeAction'
import type { MicrositeCardWithButton, MicrositeContent } from '@/utils/supabase/scanReachTypes'
import Link from 'next/link'
import React from 'react'

interface PublicMicrositeViewProps {
  content: MicrositeContent | null
  cards: MicrositeCardWithButton[]
  title: string
  micrositeId?: string
  chatbotMode?: boolean
  chatbotShowTitle?: boolean
}

export const PublicMicrositeView: React.FC<PublicMicrositeViewProps> = React.memo(
  ({ content, cards, title, micrositeId, chatbotMode = false, chatbotShowTitle = true }) => {
    if (!content) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Microsite Not Found</h1>
            <p className="text-muted-foreground">
              This microsite doesn't exist or is not published.
            </p>
          </div>
        </div>
      )
    }

    return (
      <div
        className="min-h-screen w-full flex justify-center px-4"
        style={{
          backgroundColor: content.theme_config.background,
          color: content.theme_config.text,
        }}
      >
        <div className="w-full max-w-md">
          {/* Header Image */}
          {content.header_image_url && (
            <div className="w-full flex items-center justify-center py-8">
              {/** biome-ignore lint/performance/noImgElement: <@TODO CHECK> */}
              <img
                src={content.header_image_url}
                alt="Header"
                className="object-contain max-w-full h-auto"
                style={{ maxHeight: '200px' }}
              />
            </div>
          )}

          {/* Content */}
          <div className="pb-8 space-y-8">
            {/* Title */}
            {title && (!chatbotMode || chatbotShowTitle) && (
              <h1
                className="text-3xl font-bold text-center break-words"
                style={{ color: content.theme_config.text }}
              >
                {title}
              </h1>
            )}

            {/* Chatbot or cards */}
            {chatbotMode && micrositeId ? (
              <ChatbotView
                micrositeId={micrositeId}
                primaryColor={content.theme_config.primary}
                textColor={content.theme_config.text}
                backgroundColor={content.theme_config.background}
              />
            ) : (
              <div className="space-y-6">
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className="bg-white/5 backdrop-blur-sm rounded-lg p-4 sm:p-6 space-y-4 card border overflow-hidden"
                    style={{
                      backgroundColor:
                        content.theme_config.background === '#ffffff'
                          ? '#f8f9fa'
                          : 'rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    {/* Card Title */}
                    {card.title && (
                      <h2
                        className="text-xl font-semibold break-words"
                        style={{ color: content.theme_config.text }}
                      >
                        {card.title}
                      </h2>
                    )}

                    {/* Card Content */}
                    {card.content && (
                      <div
                        className="break-words overflow-wrap-anywhere"
                        style={{ color: content.theme_config.text }}
                      >
                        {formatContent(card.content)}
                      </div>
                    )}

                    {/* Card Media */}
                    {card.media_url && (
                      <div className="w-full overflow-hidden rounded-lg">
                        {/** biome-ignore lint/performance/noImgElement: <@TODO CHECK> */}
                        <img
                          src={card.media_url}
                          alt="Card media"
                          className="w-full h-auto object-cover max-h-80"
                        />
                      </div>
                    )}

                    {/* Card Buttons */}
                    {card.buttons.length > 0 && (
                      <div className="space-y-3 pt-2 flex flex-col gap-2">
                        {card.buttons
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((button) => (
                            <Link
                              href={handleButtonClick(button.action_type, button.action_value)}
                              target={button.action_type === 'url' ? '_blank' : '_self'}
                              key={button.id}
                            >
                              <Button
                                variant="ghost"
                                size="lg"
                                className="w-full justify-start text-left py-4 px-4 sm:px-6 rounded-lg overflow-hidden"
                                style={{
                                  backgroundColor: content.theme_config.primary,
                                  color: '#ffffff',
                                  minHeight: '56px',
                                }}
                              >
                                {getButtonIcon(button.action_type)}
                                <span className="ml-3 text-base font-medium break-words flex-1 min-w-0">
                                  {button.label}
                                </span>
                              </Button>
                            </Link>
                          ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Empty State */}
                {cards.length === 0 && (
                  <div className="text-center py-12 text-opacity-70">
                    <p className="text-lg">No content available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)
